import { ExploreSingleInsight } from '@api';
import { BaseErrorView } from '@terminal/components/base-error-view/BaseErrorView';
import { ExploreInsightCard } from '@terminal/components/insights-group/insight-card/ExploreInsightCard';
import { EmptyInsights } from '@terminal/components/insights-legacy-drawer/empty-insights/EmptyInsights';
import { InsightsLoadingCard } from '@terminal/components/insights-legacy-drawer/insights-loading-card/InsightsLoadingCard';
import { IconConfig } from '@terminal/components/insights-legacy-drawer/ScenarioInsightsLegacyDrawer';
import { useQueryInsights } from '@terminal/react-query-hooks/insights/useInsight';
import { useEffect, useRef, useState } from 'react';

import styles from './InsightsList.module.scss';

export interface InsightsListProps {
  selectedInsights: ExploreSingleInsight[];
  activeInsightId: string;
  isInsightsLoading: boolean;
  handleSetActiveInsightId: (insightId: string) => void;
  handleSetIconConfig?: (config: IconConfig) => void;
}

export const InsightsList = ({
  selectedInsights,
  activeInsightId,
  isInsightsLoading,
  handleSetActiveInsightId,
  handleSetIconConfig,
}: InsightsListProps) => {
  const [initialized, setInitialized] = useState(false);
  const insightRefs = useRef<(HTMLDivElement | null)[]>([]);

  const activeElement = Object.values(insightRefs.current).find(
    ref => ref?.dataset.insightId === activeInsightId
  );

  useEffect(() => {
    if (initialized) {
      return;
    }

    if (activeElement) {
      activeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      setInitialized(true);
    }
  }, [activeElement, initialized]);

  const insightsId = selectedInsights.map(insight => insight.id);
  const { data, isFetching, isError } = useQueryInsights(
    insightsId,
    !!insightsId.length
  );
  const insights = data
    ?.slice()
    ?.sort(
      (a, b) =>
        new Date(b.created_at).valueOf() - new Date(a.created_at).valueOf()
    );

  if (isFetching || isInsightsLoading) {
    return <InsightsLoadingCard />;
  }

  if (isError) {
    return <BaseErrorView className={styles.styledBaseErrorView} />;
  }

  if (!insights.length) {
    return <EmptyInsights />;
  }

  return (
    <div className={styles.cards}>
      {insights?.map((insight, index) => {
        const exploreInsight = selectedInsights.find(s => s.id === insight.id);

        if (!exploreInsight) {
          return null;
        }

        return (
          <ExploreInsightCard
            key={insight.id}
            insight={insight}
            exploreInsight={exploreInsight}
            activeInsightId={activeInsightId}
            handleSetActiveInsightId={handleSetActiveInsightId}
            handleSetIconConfig={handleSetIconConfig}
            ref={(el: HTMLDivElement | null) => {
              insightRefs.current[index] = el;
            }}
            dataInsightId={insight.id}
          />
        );
      })}
    </div>
  );
};
